import React, { useState } from 'react';
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { useReports } from '@/hooks/useReports';
import { useTranslation } from '@/i18n';
import type { TaxReport } from '@/types/report';

function getYearOptions(t: ReturnType<typeof useTranslation>['t']) {
  const currentYear = new Date().getFullYear();
  const years = [{ value: '', label: t.app.reports.year.all }];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push({ value: y.toString(), label: y.toString() });
  }
  return years;
}

function ReportCard({
  report,
  onClick,
  t,
  formatCurrency,
  formatDate,
}: {
  report: TaxReport;
  onClick: () => void;
  t: ReturnType<typeof useTranslation>['t'];
  formatCurrency: (value: string | number | null, country: string) => string;
  formatDate: (dateString: string) => string;
}) {
  const income = parseFloat(report.totalIncome || '0');
  const expenses = parseFloat(report.totalExpenses || '0');
  const deductible = parseFloat(report.totalDeductible || '0');
  const balance = income - expenses;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardContent>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t.app.reports.title} {report.taxYear}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t.app.reports.generatedOn} {formatDate(report.generatedAt)}
              </p>
            </div>
          </div>
          <Badge variant={report.country === 'GB' ? 'info' : 'primary'}>
            {t.app.reports.country[report.country as keyof typeof t.app.reports.country]}
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <TrendingUp className="w-5 h-5 text-green-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-600 dark:text-green-400">
              {formatCurrency(income, report.country)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t.app.reports.card.income}</p>
          </div>
          <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <TrendingDown className="w-5 h-5 text-red-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-red-600 dark:text-red-400">
              {formatCurrency(expenses, report.country)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t.app.reports.card.expenses}</p>
          </div>
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <DollarSign className="w-5 h-5 text-purple-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
              {formatCurrency(deductible, report.country)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t.app.reports.card.deductible}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-secondary-700">
          <span
            className={`font-semibold ${
              balance >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {t.app.reports.card.balance}: {formatCurrency(balance, report.country)}
          </span>
          <div className="flex items-center gap-2">
            {report.pdfUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(report.pdfUrl!, '_blank');
                }}
                leftIcon={<Download className="w-4 h-4" />}
              >
                PDF
              </Button>
            )}
            {report.csvUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(report.csvUrl!, '_blank');
                }}
                leftIcon={<Download className="w-4 h-4" />}
              >
                CSV
              </Button>
            )}
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export const ReportsPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const [yearFilter, setYearFilter] = useState<string>('');
  const { data, isLoading, isError } = useReports(
    yearFilter ? parseInt(yearFilter) : undefined
  );

  const formatCurrency = (value: string | number | null, country: string = 'BR') => {
    if (value === null || value === undefined) return '-';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '-';

    const localeStr = country === 'GB' ? 'en-GB' : 'pt-BR';
    const currency = country === 'GB' ? 'GBP' : 'BRL';

    return new Intl.NumberFormat(localeStr, {
      style: 'currency',
      currency,
    }).format(numValue);
  };

  const formatDate = (dateString: string) => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-BR',
      'en-GB': 'en-GB',
      'es-ES': 'es-ES',
      'fr-FR': 'fr-FR',
      'it-IT': 'it-IT',
      'ar': 'ar-SA',
      'zh-CN': 'zh-CN',
    };
    return new Date(dateString).toLocaleDateString(localeMap[locale] || 'en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return <PageLoader message={t.app.reports.loading} />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t.app.reports.error}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {t.app.reports.errorDesc}
        </p>
      </div>
    );
  }

  const reports = data?.reports || [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.app.reports.title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.reports.subtitle}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="w-48">
          <Select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            options={getYearOptions(t)}
          />
        </div>
      </div>

      {/* Reports List */}
      {reports.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {yearFilter ? t.app.reports.noReportsFiltered : t.app.reports.noReports}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-6">
            {yearFilter
              ? t.app.reports.noReportsFilteredDesc.replace('{year}', yearFilter)
              : t.app.reports.noReportsDesc}
          </p>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg max-w-md mx-auto">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {t.app.reports.tips.desc}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid gap-6">
          {reports.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              onClick={() => {
                // Navigate to report detail or open modal
                console.log('Open report:', report.id);
              }}
              t={t}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-800">
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {t.app.reports.tips.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                {t.app.reports.tips.desc}
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <li>• {t.app.reports.tips.tip1}</li>
                <li>• {t.app.reports.tips.tip2}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
